import React, { Component } from "react";
import ReactDOM from "react-dom";
import TipTapEditor, { docThemeVars } from "../../../components/ContentEditor/TipTap/TipTapEditor";
import BrandedHeading from "../../../components/DocumentTemplateDesigner/TipTapBrandedHeading";
import DocCover from "../../../components/DocumentTemplateDesigner/TipTapDocCover";
import DocBlock from "../../../components/DocumentTemplateDesigner/TipTapDocBlock";
import FrozenChart from "../../../components/DocumentTemplateDesigner/TipTapFrozenChart";
import EditableSection from "../../../components/DocumentTemplateDesigner/TipTapEditableSection";
import {
  Sparkles,
  AlertTriangle,
  X,
  ArrowRight,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Filter,
} from "lucide-react";
import MPSSelect from "../../../components/MPSSelect";
import MPSButton from "../../../components/Buttons/Buttons/MPSButton";
import { FormattedMessage } from "react-intl";
import Messages from "../../../Shared/Messages";
import I18nProvider from "../../../services/I18nProvider";
import aiAgentsService from "../../../services/aiAgentService";
import VersionHistoryDropdown from "../../../Shared/Patients/documents/VersionHistoryDropdown";
import "./styles.scss";
// Styles for the rendered document nodes (.template-field-placeholder,
// .mps-editable-section, base .ProseMirror) — formerly pulled in by the WYSIWYG
// designer, now imported here since this is the only remaining consumer.
import "../../../components/DocumentTemplateDesigner/DocumentTemplateDesigner.scss";
import { showToaster } from "../../../State/actions/toaster";
import connect from "react-redux/es/connect/connect";
import fileService from "../../../services/fileService";
import patientsService from "../../../services/patientsService";
import ImageUrl from "../../../components/ImageUrl";
import { getAttributeValueAsString } from "../../../utils/utils";
import { getSurveyAnswerValue } from "../../../Shared/Utils";
import dateFormatService from "../../../services/dateFormatService";
import { devLog } from "../../../utils/devLog";

const SECTION_SPY_OFFSET = 88;
const SECTION_HEADING_SELECTOR = "h2.doc-chapter, h3.doc-section";
const APPROVAL_FLASH_MS = 2200;

class CanvasEditModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      editedContent: props.content,
      originalContent: props.content,
      copied: false,
      selectedSurveyType: null,
      selectedSurveyResponse: null,
      isUploading: false,
      isSavingEdits: false,
      currentDocumentId: props.generatedDocumentId || null,
      versionHistory: props.versionHistory || [],
      currentVersion: props.currentVersion || 1,
      currentVersionDate: props.lastUpdatedOn || props.createdOn || null,
      currentVersionUser: props.modifiedByUser || props.createdByUser || null,
      isRestoring: false,
      showFinalizeConfirm: false,
      patientPhotoUrl: null,
      navCollapsed: false,
      dataCollapsed: false,
      activeSectionIdx: -1,
      attrShowAll: false,
    };

    this.editorRef = React.createRef();
    this.docScrollRef = React.createRef();
  }

  componentDidMount() {
    if (this.props.isOpen) this.loadPatientPhoto();
  }

  // Fetch the patient's profile photo for the data-panel avatar (initials fallback).
  loadPatientPhoto = () => {
    const { patientId } = this.props;
    if (!patientId) return;
    patientsService
      .loadSingle(patientId)
      .then((p) => {
        const photo = p?.photos?.[0] || p?.paientPhoto;
        const url = photo?.thumbnailURL || photo?.contentURL || null;
        if (url) this.setState({ patientPhotoUrl: url });
      })
      .catch(() => {});
  };

  // Translate a Messages entry to a plain string (for toasters/title attributes).
  fmt = (msg, values) => I18nProvider.getIntlProvider().formatMessage(msg, values);

  checkHasSurveyResponses = (surveyData) => {
    if (!surveyData) return false;

    for (const surveyId of Object.keys(surveyData)) {
      const survey = surveyData[surveyId];
      if (survey._embedded?.surveyResponses?.length > 0) {
        return true;
      }
    }
    return false;
  };

  hasAiGeneratedContent = () => {
    const { documentTemplate } = this.props;
    return documentTemplate?.aiSections?.length > 0;
  };

  getAiApproval = () => {
    const doc = new DOMParser().parseFromString(this.state.editedContent || "", "text/html");
    const sections = doc.querySelectorAll('[data-editable-section][data-section-kind="ai"]');
    let approved = 0;
    sections.forEach((s) => {
      if (s.getAttribute("data-ai-approved") === "true") approved += 1;
    });
    return { total: sections.length, approved };
  };

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.setState({
        editedContent: this.props.content,
        originalContent: this.props.content,
        selectedSurveyType: null,
        selectedSurveyResponse: null,
        currentDocumentId: this.props.generatedDocumentId || null,
        versionHistory: this.props.versionHistory || [],
        currentVersion: this.props.currentVersion || 1,
        currentVersionDate: this.props.lastUpdatedOn || this.props.createdOn || null,
        currentVersionUser: this.props.modifiedByUser || this.props.createdByUser || null,
        isRestoring: false,
        showFinalizeConfirm: false,
        navCollapsed: false,
        dataCollapsed: false,
        activeSectionIdx: -1,
        attrShowAll: false,
      });
      this.loadPatientPhoto();
    }
  }

  handleContentChange = (newContent) => {
    this.setState({
      editedContent: newContent,
    });
  };

  handleCancel = () => {
    const { onClose } = this.props;
    onClose();
  };

  handleSaveEdits = async () => {
    const { editedContent, currentDocumentId } = this.state;
    const { title, patientId, documentTemplate, surveyData, attributeData, onSaveEdits } = this.props;

    if (!patientId) {
      devLog("Patient ID is required for saving edits");
      return;
    }

    this.setState({ isSavingEdits: true });

    try {
      const data = {
        id: currentDocumentId,
        title: title || this.fmt(Messages.document),
        content: editedContent,
        templateId: documentTemplate?.id,
        templateName: documentTemplate?.name,
        surveyData: surveyData,
        attributeData: attributeData,
        documentTemplate: documentTemplate,
        visibility: documentTemplate?.visibility,
        allowedCareTeams: documentTemplate?.allowedCareTeams,
      };

      const result = await aiAgentsService.saveGeneratedDocument(patientId, data);

      // Update the document ID and version history if this was a new document
      if (result) {
        this.setState({
          currentDocumentId: result.id,
          versionHistory: result.versionHistory || [],
          currentVersion: result.currentVersion || 1,
          currentVersionDate: result.lastUpdatedOn || result.createdOn || null,
          currentVersionUser: result.modifiedByUser || result.createdByUser || null,
        });
      }

      this.props.showToaster(this.fmt(Messages.doc_saved), "success", 5);

      if (onSaveEdits) {
        onSaveEdits(result);
      }

      this.handleCancel();
    } catch (error) {
      devLog("Error saving document:", error);
      this.props.showToaster(this.fmt(Messages.doc_save_failed), "error", 5);
    } finally {
      this.setState({ isSavingEdits: false });
    }
  };

  handleUploadClick = () => {
    const approval = this.getAiApproval();
    if (approval.total > 0 && approval.approved < approval.total) {
      this.flagUnapprovedSections(approval);
      return;
    }
    const { currentDocumentId } = this.state;
    const hasPreviousEdits = currentDocumentId && this.props.hasBeenSaved;
    if (hasPreviousEdits) {
      this.setState({ showFinalizeConfirm: true });
    } else {
      this.handleUpload();
    }
  };

  flagUnapprovedSections = (approval) => {
    this.props.showToaster(
      this.fmt(Messages.doc_ai_approve_before_save, {
        remaining: approval.total - approval.approved,
      }),
      "warning",
      6
    );
    const dom = this.editorRef.current?.editor?.view?.dom;
    if (!dom) return;
    const unapproved = dom.querySelectorAll(".mps-editable-section.kind-ai:not(.is-approved)");
    if (!unapproved.length) return;
    unapproved[0].scrollIntoView({ behavior: "smooth", block: "center" });
    unapproved.forEach((el) => {
      el.classList.add("needs-approval-flash");
      setTimeout(() => el.classList.remove("needs-approval-flash"), APPROVAL_FLASH_MS);
    });
  };

  handleUpload = async () => {
    const { editedContent, currentDocumentId } = this.state;
    const { title, onUploadSuccess, patientId, documentTemplate } = this.props;

    if (!patientId) {
      devLog("Patient ID is required for upload");
      return;
    }

    this.setState({ isUploading: true, showFinalizeConfirm: false });

    try {
      // Send the INNER document content + the template's header/footer images.
      // The ai-service applies branding (theme from facility appTheme + design
      // system) and wraps it into the final HTML server-side.
      const headerImage = documentTemplate?.headerImage
        ? await this.getBase64FromContent(documentTemplate.headerImage)
        : null;
      const footerImage = documentTemplate?.footerImage
        ? await this.getBase64FromContent(documentTemplate.footerImage)
        : null;

      const documentData = {
        documentTitle: title || this.fmt(Messages.document),
        content: editedContent,
        headerImage,
        footerImage,
      };

      // Use finalize endpoint which handles both cases:
      // - If currentDocumentId exists, marks the GeneratedDocument as FINALIZED
      // - Always creates PDF using the existing saveSmartDocument flow
      await aiAgentsService.finalizeGeneratedDocument(patientId, currentDocumentId, documentData);

      this.props.showToaster(this.fmt(Messages.doc_uploaded), "success", 5);
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error) {
      devLog("Error uploading document:", error);
      this.props.showToaster(this.fmt(Messages.doc_upload_failed), "error", 5);
    } finally {
      this.setState({ isUploading: false });
    }
  };

  handleRestoreVersion = async (versionNumber) => {
    const { currentDocumentId } = this.state;
    const { patientId } = this.props;

    if (!patientId || !currentDocumentId) {
      devLog("Patient ID and Document ID are required for restore");
      return;
    }

    this.setState({ isRestoring: true });

    try {
      const result = await aiAgentsService.restoreDocumentVersion(
        patientId,
        currentDocumentId,
        versionNumber
      );

      if (result) {
        this.setState({
          editedContent: result.content,
          versionHistory: result.versionHistory || [],
          currentVersion: result.currentVersion || 1,
          currentVersionDate: result.lastUpdatedOn || result.createdOn || null,
          currentVersionUser: result.modifiedByUser || result.createdByUser || null,
        });
        this.props.showToaster(this.fmt(Messages.doc_version_restored), "success", 5);
      }
    } catch (error) {
      devLog("Error restoring version:", error);
      this.props.showToaster(this.fmt(Messages.doc_version_restore_failed), "error", 5);
    } finally {
      this.setState({ isRestoring: false });
    }
  };

  getBase64FromContent = async (contentObj) => {
    if (!contentObj?.contentURL) return null;
    try {
      const blob = await fileService.getRawFile(contentObj.contentURL);
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      devLog("Error converting image to base64:", error);
      return null;
    }
  };

  renderSurveyData = (surveyData = this.props.surveyData) => {
    const { selectedSurveyType, selectedSurveyResponse } = this.state;

    if (!surveyData) {
      return (
        <div className="no-data-message">
          <p><FormattedMessage {...Messages.doc_no_survey_data} /></p>
        </div>
      );
    }

    const surveysByType = {};
    Object.keys(surveyData).forEach((surveyId) => {
      const survey = surveyData[surveyId];
      if (survey._embedded && survey._embedded.surveyResponses) {
        survey._embedded.surveyResponses.forEach((response) => {
          const typeName = response.name || response.title || this.fmt(Messages.doc_unknown_survey);
          if (!surveysByType[typeName]) {
            surveysByType[typeName] = [];
          }
          surveysByType[typeName].push(response);
        });
      }
    });

    if (Object.keys(surveysByType).length === 0) {
      return (
        <div className="no-data-message">
          <p><FormattedMessage {...Messages.doc_no_survey_responses_found} /></p>
        </div>
      );
    }

    Object.keys(surveysByType).forEach((type) => {
      surveysByType[type].sort((a, b) => b.submissionDate - a.submissionDate);
    });

    const surveyTypes = Object.keys(surveysByType);
    const currentSurveyType = selectedSurveyType || surveyTypes[0];
    const responsesForType = surveysByType[currentSurveyType] || [];

    const currentResponseId =
      selectedSurveyResponse ||
      (responsesForType.length > 0 ? responsesForType[0].id : null);
    const selectedResponse =
      responsesForType.find((r) => r.id === currentResponseId) ||
      responsesForType[0];

    const questionRows = this.extractSurveyRows(selectedResponse);

    return (
      <div className="cr-survey">
        {surveyTypes.length > 1 && (
          <MPSSelect
            value={currentSurveyType}
            onChange={(option) => {
              const newType = option.value;
              const firstResponseForType = surveysByType[newType][0];
              this.setState({
                selectedSurveyType: newType,
                selectedSurveyResponse: firstResponseForType?.id,
              });
            }}
            options={surveyTypes.map((type) => ({ value: type, label: type }))}
          />
        )}
        {responsesForType.length > 1 && (
          <MPSSelect
            value={currentResponseId}
            onChange={(option) =>
              this.setState({ selectedSurveyResponse: option.value })
            }
            options={responsesForType.map((response) => ({
              value: response.id,
              label: dateFormatService.formatDateTime(response.submissionDate),
            }))}
          />
        )}
        {selectedResponse && (
          <div className="cr-survey-card">
            <div className="cr-survey-head">
              <span className="cr-survey-name">
                {selectedResponse.title || selectedResponse.name}
              </span>
              {selectedResponse.totalScore !== undefined && (
                <span className="cr-survey-score">
                  {selectedResponse.totalScore} / {selectedResponse.totalMaxScore}
                </span>
              )}
            </div>
            <div className="cr-survey-date">
              {dateFormatService.formatDateTime(selectedResponse.submissionDate)}
            </div>
            <ul className="cr-survey-items">
              {questionRows.map((r, i) => (
                <li className="cr-survey-item" key={i}>
                  <span className="cr-survey-q">{r.text}</span>
                  <span className="cr-survey-a">
                    {r.value}
                    {r.score != null && <em> · {r.score}</em>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Flatten a survey response into compact {text, value, score} rows.
  extractSurveyRows = (response) => {
    if (!response) return [];
    const rows = [];
    (response.items || []).forEach((group) => {
      (group.items || []).forEach((item) => {
        if (item.type === "DISPLAY") return;
        const answers = item.answers || [];
        if (!answers.length) return;
        const ans = answers[0];
        let value;
        try {
          value = getSurveyAnswerValue(item.type, ans);
        } catch (e) {
          value = ans.valueString;
        }
        const score = ans.score != null ? ans.score : ans.scoreDouble;
        rows.push({ text: item.text, value, score });
      });
    });
    return rows;
  };

  getThemeColor = () => {
    const raw = this.props.appTheme?.themeColor;
    return raw ? raw.replace("0x", "#") : "";
  };

  getAttributes = () => this.props.attributeData?._embedded?.uIAttributes || [];

  computeSections = () => {
    const content = this.state.editedContent || "";
    if (this._sectionsCache && this._sectionsCache.content === content) {
      return this._sectionsCache.sections;
    }
    const sections = [];
    try {
      const doc = new DOMParser().parseFromString(content, "text/html");
      const nodes = doc.body.querySelectorAll(
        `${SECTION_HEADING_SELECTOR}, [data-section-kind="ai"], [data-survey-chart], [field], [data-interp-field]`
      );
      const addCsv = (set, csv) =>
        (csv || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
          .forEach((v) => set.add(v));
      let current = null;
      nodes.forEach((node) => {
        const tag = (node.tagName || "").toLowerCase();
        const isHeading =
          (tag === "h2" && node.classList.contains("doc-chapter")) ||
          (tag === "h3" && node.classList.contains("doc-section"));
        if (isHeading) {
          current = { label: (node.textContent || "").trim(), fields: new Set(), surveys: new Set() };
          sections.push(current);
        } else if (!current) {
          // content before the first heading is unscoped
        } else if (node.getAttribute("data-section-kind") === "ai") {
          addCsv(current.fields, node.getAttribute("data-ai-attributes"));
          addCsv(current.surveys, node.getAttribute("data-ai-surveys"));
        } else if (node.hasAttribute("data-survey-chart")) {
          const s = node.getAttribute("data-survey-chart");
          if (s) current.surveys.add(s);
        } else {
          const f = node.getAttribute("field") || node.getAttribute("data-interp-field");
          if (f) current.fields.add(f);
        }
      });
    } catch (e) {
      devLog("computeSections parse failed", e);
    }
    this._sectionsCache = { content, sections };
    return sections;
  };

  handleDocScroll = () => {
    if (this._scrollRaf) return;
    this._scrollRaf = requestAnimationFrame(() => {
      this._scrollRaf = null;
      const container = this.docScrollRef.current;
      const dom = this.editorRef.current?.editor?.view?.dom;
      if (!container || !dom) return;
      const heads = dom.querySelectorAll(SECTION_HEADING_SELECTOR);
      const triggerY = container.getBoundingClientRect().top + SECTION_SPY_OFFSET;
      let idx = -1;
      for (let i = 0; i < heads.length; i += 1) {
        if (heads[i].getBoundingClientRect().top <= triggerY) idx = i;
        else break;
      }
      if (idx !== this.state.activeSectionIdx) {
        this.setState({ activeSectionIdx: idx, attrShowAll: false });
      }
    });
  };

  findAttr = (name) => this.getAttributes().find((a) => a.name === name);

  getPatientName = () => {
    const a = this.findAttr("MPS_PATIENT_NAME");
    return (a && getAttributeValueAsString(a)) || this.fmt(Messages.doc_this_patient);
  };

  getGeneratedDateStr = () => {
    const d = this.state.currentVersionDate || this.props.createdOn;
    return d ? dateFormatService.formatDate(d) : "";
  };

  // Parse the document into a jump-to outline (chapters + sections + AI blocks),
  // flagging sections that need input (empty fill-ins) or AI review.
  buildOutline = () => {
    const doc = new DOMParser().parseFromString(this.state.editedContent || "", "text/html");
    const items = [];
    let chapter = 0;
    let current = null;

    // Query in document order, at any depth — chapters, sections, editable
    // (AI / free-text) blocks, and data blocks (for the "needs input" flag).
    const nodes = doc.body.querySelectorAll(
      "h2.doc-chapter, h3.doc-section, [data-editable-section], [data-doc-block]"
    );

    nodes.forEach((node) => {
      const tag = (node.tagName || "").toLowerCase();

      if (tag === "h2" && node.classList.contains("doc-chapter")) {
        chapter += 1;
        current = {
          key: `i${items.length}`,
          selector: "h2.doc-chapter",
          label: (node.textContent || "").replace(/^\s*\d+\.\s*/, "").trim() || `Section ${chapter}`,
          level: 1,
          number: chapter,
          flag: null,
        };
        items.push(current);
      } else if (tag === "h3" && node.classList.contains("doc-section")) {
        current = {
          key: `i${items.length}`,
          selector: "h3.doc-section",
          label: (node.textContent || "").trim() || "Section",
          level: 2,
          number: null,
          flag: null,
        };
        items.push(current);
      } else if (node.hasAttribute("data-editable-section")) {
        // kind defaults to "free" when the attribute is absent.
        const kind = node.getAttribute("data-section-kind") || "free";
        if (kind === "ai") {
          const heading = node.querySelector("h1,h2,h3,h4,h5,h6");
          const isApproved = node.getAttribute("data-ai-approved") === "true";
          const labelAttr = (node.getAttribute("data-label") || "").replace(/\s*—\s*review & edit$/, "");
          items.push({
            key: `i${items.length}`,
            // NodeView renders class, not data-* attrs — match the live DOM class.
            selector: ".mps-editable-section.kind-ai",
            label: (heading && heading.textContent.trim()) || labelAttr || "AI Section",
            level: 2,
            number: null,
            flag: isApproved ? "ai-ok" : "ai",
          });
        } else {
          items.push({
            key: `i${items.length}`,
            selector: ".mps-editable-section.kind-free",
            label: node.getAttribute("data-label") || "Free text",
            level: 2,
            number: null,
            flag: "input",
          });
        }
      } else if (node.hasAttribute("data-doc-block") && current && current.flag !== "input") {
        // A data block with an unfilled fill-in field flags its section for input.
        const fillables = node.querySelectorAll(".mps-fillable");
        const anyEmpty = Array.from(fillables).some((f) => !(f.textContent || "").trim());
        if (anyEmpty) current.flag = "input";
      }
    });
    return items;
  };

  // Scroll the editor to the Nth heading/section of the given selector.
  jumpTo = (selector, occurrence) => {
    const dom = this.editorRef.current?.editor?.view?.dom;
    if (!dom) return;
    const matches = dom.querySelectorAll(selector);
    const el = matches[occurrence];
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  renderSectionNav = (outline) => {
    if (!outline || !outline.length) return null;
    if (this.state.navCollapsed) {
      return (
        <aside className="cr-rail cr-rail-left">
          <button
            className="cr-rail-toggle"
            onClick={() => this.setState({ navCollapsed: false })}
            title={this.fmt(Messages.doc_toggle_sections)}
          >
            <PanelLeftOpen size={18} />
          </button>
          <span className="cr-rail-label">
            <FormattedMessage {...Messages.doc_report_sections} />
          </span>
        </aside>
      );
    }
    // occurrence index per selector so jumpTo can find the right element
    const counters = {};
    return (
      <aside className="cr-nav">
        <div className="cr-nav-title">
          <FormattedMessage {...Messages.doc_report_sections} />
          <button
            className="cr-head-collapse"
            onClick={() => this.setState({ navCollapsed: true })}
            title={this.fmt(Messages.doc_toggle_sections)}
          >
            <PanelLeftClose size={16} />
          </button>
        </div>
        <ul className="cr-nav-list">
          {outline.map((item) => {
            const occ = counters[item.selector] || 0;
            counters[item.selector] = occ + 1;
            return (
              <li
                key={item.key}
                className={`cr-nav-item lvl${item.level}`}
                onClick={() => this.jumpTo(item.selector, occ)}
              >
                {item.number != null ? (
                  <span className="cr-nav-num">{item.number}</span>
                ) : (
                  <span className="cr-nav-bullet" />
                )}
                <span className="cr-nav-label">{item.label}</span>
                {item.flag === "ai" && <span className="cr-flag ai" title={this.fmt(Messages.doc_flag_ai_verify)} />}
                {item.flag === "ai-ok" && (
                  <span className="cr-flag ai-ok" title={this.fmt(Messages.doc_ai_approved)} />
                )}
                {item.flag === "input" && (
                  <span className="cr-flag input" title={this.fmt(Messages.doc_flag_needs_input)} />
                )}
              </li>
            );
          })}
        </ul>
        <div className="cr-nav-legend">
          <span>
            <span className="cr-flag ai" /> <FormattedMessage {...Messages.doc_flag_ai_verify} />
          </span>
          <span>
            <span className="cr-flag input" /> <FormattedMessage {...Messages.doc_flag_needs_input} />
          </span>
        </div>
      </aside>
    );
  };

  renderAvatar = () => {
    const name = this.getPatientName();
    const initials = name
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase();
    return this.state.patientPhotoUrl ? (
      <ImageUrl
        url={this.state.patientPhotoUrl}
        imageStyle={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
    ) : (
      initials || "?"
    );
  };

  renderPatientPanel = () => {
    const name = this.getPatientName();
    if (this.state.dataCollapsed) {
      return (
        <aside className="cr-rail cr-rail-right">
          <button
            className="cr-rail-toggle"
            onClick={() => this.setState({ dataCollapsed: false })}
            title={this.fmt(Messages.doc_toggle_patient_data)}
          >
            <PanelRightOpen size={18} />
          </button>
          <div className="cr-rail-avatar">{this.renderAvatar()}</div>
          <span className="cr-rail-label">
            <FormattedMessage {...Messages.doc_patient_data} />
          </span>
        </aside>
      );
    }
    const allAttributes = this.getAttributes().filter((a) => a.name !== "MPS_PATIENT_NAME");
    const sections = this.computeSections();
    const active = this.state.activeSectionIdx >= 0 ? sections[this.state.activeSectionIdx] : null;
    const sectionAttrs = active ? allAttributes.filter((a) => active.fields.has(a.name)) : [];
    const allSurveyData = this.props.surveyData || {};
    const sectionSurveyData =
      active && active.surveys.size
        ? Object.keys(allSurveyData)
            .filter((id) => active.surveys.has(id))
            .reduce((acc, id) => ({ ...acc, [id]: allSurveyData[id] }), {})
        : {};
    const canFilter = !!active && (sectionAttrs.length > 0 || Object.keys(sectionSurveyData).length > 0);
    const filtered = canFilter && !this.state.attrShowAll;
    const attributes = filtered ? sectionAttrs : allAttributes;
    const surveyData = filtered ? sectionSurveyData : allSurveyData;
    const scopeLabel = active ? active.label.replace(/^\s*\d+\.\s*/, "") : "";
    const hasSurveyData = this.checkHasSurveyResponses(surveyData);
    const showAttrs = !filtered || attributes.length > 0;
    return (
      <aside className="cr-data">
        <div className="cr-data-head">
          <span className="cr-data-title">
            <FormattedMessage {...Messages.doc_patient_data} />
          </span>
          <button
            className="cr-head-collapse"
            onClick={() => this.setState({ dataCollapsed: true })}
            title={this.fmt(Messages.doc_toggle_patient_data)}
          >
            <PanelRightClose size={16} />
          </button>
        </div>
        <div className="cr-patient">
          <div className="cr-avatar">{this.renderAvatar()}</div>
          <div className="cr-patient-name">{name}</div>
        </div>
        {canFilter && (
          <div className={`cr-attr-filter${filtered ? " is-filtered" : ""}`}>
            <span className="cr-attr-chip">
              <Filter size={12} />
              <span className="cr-attr-chip-text">
                {filtered ? scopeLabel : this.fmt(Messages.doc_attr_all_label)}
              </span>
            </span>
            <button
              className="cr-attr-toggle"
              onClick={() => this.setState({ attrShowAll: !this.state.attrShowAll })}
            >
              {filtered
                ? this.fmt(Messages.doc_scope_show_all)
                : this.fmt(Messages.doc_attr_show_section)}
            </button>
          </div>
        )}
        {showAttrs && (
          <>
            <div className="cr-section-label">
              <FormattedMessage {...Messages.attributes} />
              <span className="cr-attr-count">{attributes.length}</span>
            </div>
            {attributes.length ? (
              <div className="cr-demos">
                {attributes.map((a) => {
                  const color = a.value?.valueObservation?.interpretation?.associatedColor;
                  return (
                    <div className="cr-demo" key={a.id || a.name}>
                      <div className="cr-demo-label">{a.title || a.name}</div>
                      <div className="cr-demo-value">
                        {getAttributeValueAsString(a) || "N/A"}
                        {color && <span className="cr-demo-dot" style={{ background: color }} />}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="cr-empty-note">
                <FormattedMessage {...Messages.doc_no_attributes_configured} />
              </div>
            )}
          </>
        )}
        {hasSurveyData && (
          <>
            <div className="cr-section-label">
              <FormattedMessage {...Messages.doc_survey_responses} />
            </div>
            <div className="cr-surveys">{this.renderSurveyData(surveyData)}</div>
          </>
        )}
      </aside>
    );
  };

  render() {
    const { isOpen, title, isRTL } = this.props;
    const {
      editedContent,
      isUploading,
      isSavingEdits,
      versionHistory,
      currentVersion,
      currentVersionDate,
      currentVersionUser,
      isRestoring,
      currentDocumentId,
      showFinalizeConfirm,
      navCollapsed,
      dataCollapsed,
    } = this.state;
    if (!isOpen) return null;
    const themeColor = this.getThemeColor();
    const patientName = this.getPatientName();
    const genDate = this.getGeneratedDateStr();
    const hasAi = this.hasAiGeneratedContent();
    const aiApproval = this.getAiApproval();
    const approvalsPending = aiApproval.total > 0 && aiApproval.approved < aiApproval.total;
    // Older documents have no recognised section structure — there's no nav to show.
    const navOutline = this.buildOutline();
    // Left nav / right data panel collapse to a thin labelled rail (not hidden).
    const hasNav = navOutline.length > 0;
    const leftCol = hasNav ? (navCollapsed ? "48px" : "250px") : null;
    const bodyCols = [leftCol, "1fr", dataCollapsed ? "48px" : "320px"]
      .filter(Boolean)
      .join(" ");

    return ReactDOM.createPortal(
      <div
        className={`canvas-review ${isRTL ? "rtl" : ""}`}
        style={docThemeVars(themeColor)}
        dir={isRTL ? "rtl" : "ltr"}
      >
        <div className="cr-shell">
          <div className="cr-topbar">
            <div className="cr-title-block">
              <h2 className="cr-title">{title || this.fmt(Messages.document)}</h2>
              <span className="cr-subtitle">
                <FormattedMessage {...Messages.doc_generated_for} values={{ name: patientName }} />
                {genDate ? ` · ${genDate}` : ""}
              </span>
            </div>
            <div className="cr-topbar-right">
              {hasAi && (
                <span className="cr-ai-pill">
                  <Sparkles size={14} />
                  <FormattedMessage {...Messages.doc_ai_content_warning} />
                </span>
              )}
              {currentDocumentId && (versionHistory?.length > 0 || currentVersion > 1) && (
                <VersionHistoryDropdown
                  currentVersion={currentVersion}
                  currentVersionDate={currentVersionDate}
                  currentVersionUser={currentVersionUser}
                  versions={versionHistory}
                  onRestoreVersion={this.handleRestoreVersion}
                  isRestoring={isRestoring}
                  isRTL={isRTL}
                  previewThemeStyle={docThemeVars(themeColor)}
                />
              )}
              <button className="cr-close" onClick={this.handleCancel} title={this.fmt(Messages.close)}>
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="cr-body" style={{ gridTemplateColumns: bodyCols }}>
            {hasNav && this.renderSectionNav(navOutline)}
            <main className="cr-doc" ref={this.docScrollRef} onScroll={this.handleDocScroll}>
              <div className="cr-page">
                <TipTapEditor
                  value={editedContent}
                  onChange={this.handleContentChange}
                  editorRef={this.editorRef}
                  minimalEditor={true}
                  hideMenuBar={true}
                  additionalExtensions={[
                    BrandedHeading,
                    DocCover.configure({ hidden: true }),
                    // Data blocks are deterministic from patient data — show them
                    // frozen (read-only) in review; only AI prose is editable.
                    DocBlock.configure({ readonly: true }),
                    FrozenChart,
                    // Clearly-marked editable zones: AI drafts + free-text sections.
                    EditableSection,
                  ]}
                  style={{ textAlign: isRTL ? "right" : "left" }}
                />
              </div>
            </main>
            {this.renderPatientPanel()}
          </div>
          <div className="cr-footer">
            {showFinalizeConfirm ? (
              <div className="canvas-finalize-confirm">
                <div className="canvas-finalize-confirm-content">
                  <AlertTriangle size={16} className="canvas-finalize-confirm-icon" />
                  <span className="canvas-finalize-confirm-text">
                    <FormattedMessage {...Messages.finalize_document_disclaimer} />
                  </span>
                </div>
                <div className="canvas-finalize-confirm-actions">
                  <MPSButton
                    variant="secondary"
                    size="md"
                    onClick={() => this.setState({ showFinalizeConfirm: false })}
                    className="cr-btn"
                  >
                    <FormattedMessage {...Messages.cancel} />
                  </MPSButton>
                  <MPSButton
                    variant="primary"
                    size="md"
                    onClick={this.handleUpload}
                    disabled={isUploading}
                    className="cr-btn primary"
                  >
                    {isUploading ? (
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="sr-only">Uploading...</span>
                      </div>
                    ) : (
                      <FormattedMessage {...Messages.confirm_finalize} />
                    )}
                  </MPSButton>
                </div>
              </div>
            ) : (
              <div className="cr-footer-actions">
              {approvalsPending && (
                <button
                  type="button"
                  className="cr-approve-note"
                  onClick={() => this.flagUnapprovedSections(aiApproval)}
                  title={this.fmt(Messages.doc_ai_jump_unapproved)}
                >
                  <AlertTriangle size={13} />
                  <FormattedMessage
                    {...Messages.doc_ai_approvals_pending}
                    values={{ approved: aiApproval.approved, total: aiApproval.total }}
                  />
                </button>
              )}
              <MPSButton
                variant="secondary"
                size="md"
                onClick={this.handleSaveEdits}
                disabled={isSavingEdits}
                title={this.fmt(Messages.save_edits)}
                className="cr-btn"
              >
                {isSavingEdits ? (
                  <FormattedMessage {...Messages.saving} />
                ) : (
                  <FormattedMessage {...Messages.save_edits} />
                )}
              </MPSButton>
              <MPSButton
                variant="primary"
                size="md"
                onClick={this.handleUploadClick}
                disabled={isUploading}
                title={
                  approvalsPending
                    ? this.fmt(Messages.doc_ai_approvals_pending, {
                        approved: aiApproval.approved,
                        total: aiApproval.total,
                      })
                    : this.fmt(Messages.save_to_patient_documents)
                }
                className={`cr-btn primary${approvalsPending ? " is-gated" : ""}`}
              >
                <FormattedMessage {...Messages.save_to_patient_documents} />
                <ArrowRight size={16} style={{ marginInlineStart: 8 }} />
              </MPSButton>
              </div>
            )}
          </div>
        </div>
      </div>,
      document.body
    );
  }
}
const actions = {
  showToaster: showToaster,
};

const mapStateToProps = (state) => ({
  appTheme: state.user?.data?.appTheme || {},
});

export default connect(mapStateToProps, actions)(CanvasEditModal);
